import React from 'react'

/**
 * The pin, as a literal path — the same construction lucide's `MapPin` uses, at a smaller
 * radius so an item fits under it on the same 24 grid.
 *
 * Head centre (15, 6.6), radius 3.6. Lucide puts the tip at **1.5r** below the centre and
 * the first control point at **0.75r** (`M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0`,
 * r=8), which is the proportion that makes the shape read as a pin rather than a balloon.
 * Scaled to r=3.6 that is 5.4 and 2.7, so the tip lands at y=12.
 *
 * Written out rather than computed from the constants: `3.6 * 0.75` is `2.7000000000000002`
 * in floating point, and a path attribute full of that noise is worse than arithmetic in a
 * comment.
 */
const PIN_PATH = 'M18.6 6.6c0 2.7-3.6 5.4-3.6 5.4s-3.6-2.7-3.6-5.4a3.6 3.6 0 0 1 7.2 0'

/**
 * An item with a location pin planted on it — "mark where the tag sits on this object".
 *
 * The empty state of the scan-media field, replacing lucide's `SmartphoneNfc`. The phone
 * glyph named the WRONG half of the picture: what the brand is being asked to upload is a
 * shot of their own product with the tag's position shown on it, not a picture of a phone.
 * A customer holding a phone is what the media EXPLAINS, not what it contains.
 *
 * Lucide has no product-with-pin, so this is drawn here — but drawn to lucide's own spec so
 * it sits beside the rest of the app's icons without reading as an outlier: a 24×24 grid,
 * `fill="none"` with `stroke="currentColor"`, round caps and joins, and a caller-set
 * `strokeWidth` (1 at every call site here, matching the house weight). `size` and
 * `strokeWidth` are the only props because that is the contract `FileUploadField`'s `icon`
 * prop takes: `React.ComponentType<{ size?: number; strokeWidth?: number }>`.
 *
 * Deliberately GENERIC — a soft-cornered silhouette, not a shoe or a shirt. It stands in for
 * whatever the brand sells, and a specific object would be wrong for most of them.
 *
 * ## Sized for 24px, which is where it actually renders
 *
 * `FileUploadField` draws its prompt icon at 24 when the dropzone is under 200px tall, and
 * the scan-media dropzone is 140. So the geometry is chosen for legibility at 24 CSS px:
 * one closed silhouette, one pin, and one hole in the pin (without the hole the pin reads as
 * a water droplet). No internal seams or fold lines — at this size they turn to noise.
 *
 * The pin's tip (y=12) lands 1 unit INSIDE the item's top edge (y=11) rather than resting on
 * it, so the two shapes read as one scene ("planted on it") instead of two stacked objects.
 * One unit and not more: every unit of overlap is another line crossing in a 24px picture.
 * Margins come out even — the drawing spans x 3.5–20.5 and y 3–21 on a 24 grid.
 */
export type TagLocationIconProps = {
  /** Rendered size in px. Square, like every lucide glyph. */
  size?: number
  strokeWidth?: number
  /**
   * An accessible name. Omit it (the default) for a decorative mark sitting next to its own
   * label — the dropzone prompt is labelled in text beside it, so the icon stays out of the
   * accessibility tree rather than repeating it.
   */
  title?: string
  className?: string
}

export const TagLocationIcon = ({
  size = 24,
  strokeWidth = 1,
  title,
  className,
}: TagLocationIconProps) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    xmlns="http://www.w3.org/2000/svg"
    role={title ? 'img' : undefined}
    aria-hidden={title ? undefined : true}
    focusable="false"
  >
    {title ? <title>{title}</title> : null}
    {/* The item. Drawn FIRST so the pin's tip overlaps it, not the other way round. */}
    <rect x="3.5" y="11" width="17" height="10" rx="2.8" />
    <path d={PIN_PATH} />
    {/*
      r=1.6, NOT lucide's proportion.

      `MapPin` uses r=3 inside r=8, a ratio of 0.375, and this pin's head is r=3.6 — so
      matching the ratio would put the hole at 1.35, which is under 1.4 CSS px at the 24px
      size this actually renders at. Measured against the rendered glyph: at that width the
      hole closes up and the pin reads as a water droplet, which is the one thing the hole
      exists to prevent. 1.6 (ratio 0.44) survives the size. The pin is less than half
      lucide's, so its interior cannot be scaled from lucide's.
    */}
    <circle cx="15" cy="6.6" r="1.6" />
  </svg>
)
